import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faFileDownload, faFileUpload, faSearch, faFilter, faSync } from '@fortawesome/free-solid-svg-icons';
import { displayPeriod, parsePeriodValue, formatPeriod, formatDuration } from '../../utils/periodUtils';

function ContractList({ contracts, onDelete, onEdit, onAddClick, onRefresh, isRefreshing }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    let date = new Date(dateString);
    if (isNaN(date.getTime()) && typeof dateString === 'string') {
      const parts = dateString.split(/[\/\s.]/);
      if (parts.length >= 3) {
        date = new Date();
      }
    }
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter records based on active criteria
  const filteredContracts = contracts.filter((item) => {
    const matchesRef = item.referenceNo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDiscipline = disciplineFilter === '' || item.discipline === disciplineFilter;
    const matchesVendor = vendorSearch === '' || item.vendors.some(v => v.toLowerCase().includes(vendorSearch.toLowerCase()));
    
    return matchesRef && matchesDiscipline && matchesVendor;
  });

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredContracts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);

  // Statistics calculation
  const totalAPVSum = filteredContracts.reduce((sum, item) => sum + parseFloat(item.totalApv || 0), 0);
  const totalBaseSum = filteredContracts.reduce((sum, item) => sum + parseFloat(item.baseAmount || 0), 0);
  const totalOptionSum = filteredContracts.reduce((sum, item) => sum + parseFloat(item.optionAmount || 0), 0);
  const totalContingencySum = filteredContracts.reduce((sum, item) => sum + parseFloat(item.contingencyAmount || 0), 0);

  // Discipline CSS class mapper
  const getDisciplineClass = (discipline) => {
    switch (discipline) {
      case 'M&E (Mechanical & Electrical)':
      case 'M&E':
        return 'me';
      case 'Architecture':
      case 'Arch':
        return 'arch';
      case 'Contractor':
        return 'contractor';
      case 'Quantity Surveying':
      case 'QS':
        return 'qs';
      case 'Civil & Structural':
      case 'C&S':
        return 'cs';
      default:
        return '';
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Discipline',
      'Reference No',
      'Start Date',
      'End Date',
      'Duration',
      'Base Period',
      'Base Amount ($)',
      'Option Period',
      'Option Amount ($)',
      'Contingency (%)',
      'Contingency Amount ($)',
      'Total APV ($)',
      'Vendors',
      'Remarks'
    ];

    const rows = filteredContracts.map(c => [
      c.discipline,
      c.referenceNo,
      c.startDate,
      c.endDate,
      formatDuration(c.duration || { months: c.durationTotalMonths || c.durationMonths, weeks: c.durationWeeks }),
      formatDuration(c.basePeriod || { months: c.basePeriodTotalMonths || c.basePeriodMonths, weeks: c.basePeriodWeeks }),
      c.baseAmount,
      formatDuration(c.optionPeriod || { months: c.optionPeriodTotalMonths || c.optionPeriodMonths, weeks: c.optionPeriodWeeks }),
      c.optionAmount,
      c.contingencyPercentage,
      c.contingencyAmount,
      c.totalApv,
      c.vendors.join('; '),
      c.remarks
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `CPMS_APV_Contracts_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import from CSV
  const handleCSVImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n');
      if (lines.length <= 1) return;

      const importedContracts = [];
      const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));

      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;

        // Custom csv parsing to handle quoted fields with commas
        const matches = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
        if (!matches || matches.length < 5) continue;

        const row = matches.map(m => m.trim().replace(/^["']|["']$/g, '').replace(/""/g, '"'));
        
        // Match columns
        const discipline = row[0] || 'M&E';
        const referenceNo = row[1] || `APV-IMPORT-${Date.now()}-${i}`;
        const startDate = row[2] || new Date().toISOString().split('T')[0];
        const endDate = row[3] || new Date().toISOString().split('T')[0];
        
        const parsedDur = parsePeriodValue(row[4] || 0);
        const parsedBase = parsePeriodValue(row[5] || 0);
        const baseAmount = parseFloat(row[6] || 0);
        const parsedOpt = parsePeriodValue(row[7] || 0);
        const optionAmount = parseFloat(row[8] || 0);
        const contingencyPercentage = parseFloat(row[9] || 5);
        const contingencyAmount = parseFloat(row[10] || 0);
        const totalApv = parseFloat(row[11] || 0);
        const vendors = row[12] ? row[12].split(';').map(v => v.trim()) : [];
        const remarks = row[13] || '';

        importedContracts.push({
          id: `csv-${Date.now()}-${i}`,
          discipline,
          referenceNo,
          startDate,
          endDate,
          duration: formatPeriod(0, parsedDur.totalMonths, parsedDur.weeks),
          durationYears: 0,
          durationMonths: parsedDur.totalMonths,
          durationWeeks: parsedDur.weeks,
          durationTotalMonths: parsedDur.totalMonths,
          durationTotalWeeks: parsedDur.totalWeeks,
          basePeriod: formatPeriod(0, parsedBase.totalMonths, parsedBase.weeks),
          basePeriodYears: 0,
          basePeriodMonths: parsedBase.totalMonths,
          basePeriodWeeks: parsedBase.weeks,
          basePeriodTotalMonths: parsedBase.totalMonths,
          basePeriodTotalWeeks: parsedBase.totalWeeks,
          baseAmount,
          optionPeriod: formatPeriod(0, parsedOpt.totalMonths, parsedOpt.weeks),
          optionPeriodYears: 0,
          optionPeriodMonths: parsedOpt.totalMonths,
          optionPeriodWeeks: parsedOpt.weeks,
          optionPeriodTotalMonths: parsedOpt.totalMonths,
          optionPeriodTotalWeeks: parsedOpt.totalWeeks,
          optionAmount,
          contingencyPercentage,
          contingencyAmount,
          totalApv,
          vendors,
          remarks
        });
      }

      if (importedContracts.length > 0) {
        importedContracts.forEach(newContract => {
          // Check duplicates by reference number
          onAddClick(newContract);
        });
        alert(`Successfully imported ${importedContracts.length} records!`);
      }
    };
    reader.readAsText(file);
    // Reset file input value so same file can be selected again
    event.target.value = '';
  };

  return (
    <div className="apv-container">
      {/* Dashboard KPI cards */}
      {/* <div className="apv-stats-grid">
        <div className="apv-stat-card contracts">
          <div className="apv-stat-label">Total Contracts</div>
          <div className="apv-stat-value">{filteredContracts.length}</div>
        </div>
        <div className="apv-stat-card">
          <div className="apv-stat-label">Base Amount Sum</div>
          <div className="apv-stat-value">{formatCurrency(totalBaseSum)}</div>
        </div>
        <div className="apv-stat-card">
          <div className="apv-stat-label">Option Amount Sum</div>
          <div className="apv-stat-value">{formatCurrency(totalOptionSum)}</div>
        </div>
        <div className="apv-stat-card">
          <div className="apv-stat-label">Contingency Sum</div>
          <div className="apv-stat-value">{formatCurrency(totalContingencySum)}</div>
        </div>
        <div className="apv-stat-card total-apv">
          <div className="apv-stat-label">Total APV Sum</div>
          <div className="apv-stat-value" style={{ color: '#7030a0' }}>{formatCurrency(totalAPVSum)}</div>
        </div>
      </div> */}

      {/* Main Records Card */}
      <div className="apv-table-card">
        <div className="apv-table-header-band d-flex justify-content-between align-items-center">
          <div>
            <h3>Contract Records</h3>
            <p>View, edit, and manage all contract submissions</p>
          </div>
          <div className="d-flex gap-2">
            {onRefresh && (
              <button 
                className="btn btn-light btn-sm text-secondary" 
                onClick={onRefresh} 
                disabled={isRefreshing}
                title="Refresh Contract List"
              >
                <FontAwesomeIcon icon={faSync} spin={isRefreshing} className="me-1" />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            )}
            <button className="btn btn-light btn-sm text-success" onClick={exportToCSV} title="Export to CSV">
              <FontAwesomeIcon icon={faFileDownload} className="me-1" /> Export
            </button>
            <label className="btn btn-light btn-sm text-primary mb-0" title="Import CSV" style={{ cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faFileUpload} className="me-1" /> Import
              <input type="file" accept=".csv" onChange={handleCSVImport} style={{ display: 'none' }} />
            </label>
            <button className="btn btn-primary btn-sm" onClick={() => onEdit(null)}>
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Contract
            </button>
          </div>
        </div>

        {/* Filter Toolbar */}
        {/* <div className="apv-filters">
          <div className="apv-filter-group">
            <label>Discipline</label>
            <select value={disciplineFilter} onChange={(e) => setDisciplineFilter(e.target.value)}>
              <option value="">All Disciplines</option>
              <option value="M&E (Mechanical & Electrical)">M&E</option>
              <option value="Architecture">Architecture</option>
              <option value="Contractor">Contractor</option>
              <option value="Quantity Surveying">Quantity Surveying</option>
              <option value="Civil & Structural">Civil & Structural</option>
            </select>
          </div>
          <div className="apv-filter-group">
            <label>Search Reference No</label>
            <div className="position-relative">
              <input 
                type="text" 
                placeholder="Search ref no..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
          </div>
          <div className="apv-filter-group">
            <label>Search Vendor</label>
            <input 
              type="text" 
              placeholder="Search vendor name..." 
              value={vendorSearch} 
              onChange={(e) => setVendorSearch(e.target.value)} 
            />
          </div>
        </div> */}

        {/* Table representation */}
        <div className="apv-table-wrapper">
          <table className="apv-table">
            <thead>
              <tr>
                <th>Discipline</th>
                <th>Reference No</th>
                <th>Contract Period</th>
                <th>Base Period</th>
                <th>Option Period</th>
                <th>Base Amount</th>
                <th>Vendors</th>
                <th>Total APV</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <span className={`discipline-pill ${getDisciplineClass(item.discipline)}`}>
                        {item.discipline === 'M&E (Mechanical & Electrical)' ? 'M&E' : 
                         item.discipline === 'Civil & Structural' ? 'C&S' : 
                         item.discipline === 'Quantity Surveying' ? 'QS' : 
                         item.discipline === 'Architecture' ? 'Arch' : item.discipline}
                      </span>
                    </td>
                    <td style={{ fontWeight: '600' }}>{item.referenceNo}</td>
                    <td>
                      <div>
                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                      </div>
                      <small style={{ color: '#7f7f7f' }}>
                        ({formatDuration(item.duration || { months: item.durationTotalMonths || item.durationMonths, weeks: item.durationWeeks })})
                      </small>
                    </td>
                    <td>{formatDuration(item.basePeriod || { months: item.basePeriodTotalMonths || item.basePeriodMonths, weeks: item.basePeriodWeeks })}</td>
                    <td>{formatDuration(item.optionPeriod || { months: item.optionPeriodTotalMonths || item.optionPeriodMonths, weeks: item.optionPeriodWeeks })}</td>
                    <td style={{ fontWeight: '500' }}>{formatCurrency(item.baseAmount)}</td>
                    <td>
                      <div className="vendor-tags-list">
                        {item.vendors.map((vendor, idx) => (
                          <span key={idx} className="vendor-tag">{vendor}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontWeight: '700', color: '#7030a0' }}>{formatCurrency(item.totalApv)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="apv-action-btn edit" onClick={() => onEdit(item)} title="Edit Record">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="apv-action-btn delete" onClick={() => onDelete(item.id)} title="Delete Record">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '30px', color: '#7f7f7f' }}>
                    No contract records found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer and Pagination */}
        <div className="apv-table-footer">
          <div>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredContracts.length)} of {filteredContracts.length} records
          </div>
          {totalPages > 1 && (
            <div className="apv-pagination">
              <button 
                className="apv-page-link" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i} 
                  className={`apv-page-link ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="apv-page-link" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContractList;
